import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';

function Navbar({ isSidebarOpen, toggleSidebar, handleLogout }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [flashMessage, setFlashMessage] = useState('');
  const [showFlash, setShowFlash] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const audioUnlocked = useRef(false);
  const location = useLocation();

  const STORAGE_KEY = 'instant_transfer_audio_unlocked';

  useEffect(() => {
    const unlocked = localStorage.getItem(STORAGE_KEY) === 'true';
    if (unlocked) {
      audioUnlocked.current = true;
    }
  }, []);

  const unlockAudio = () => {
    if (audioUnlocked.current) return;

    const silent = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=');
    silent.play().then(() => {
      audioUnlocked.current = true;
      localStorage.setItem(STORAGE_KEY, 'true');
      console.log('Audio unlocked permanently');
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
    }).catch(() => {});
  };

  const playSound = () => {
    if (!audioRef.current || !audioUnlocked.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  useEffect(() => {
    if (!audioUnlocked.current) {
      document.addEventListener('click', unlockAudio);
      document.addEventListener('touchstart', unlockAudio);
    }

    const socket = io('https://instant-transfer-back-production.up.railway.app', {
      transports: ['websocket'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      socket.emit('joinAdmin');
    });

    socket.on('newNotification', (notif) => {
      if (notif.adminRead) return;

      setUnreadCount(prev => prev + 1);

      if (location.pathname !== '/notifications') {
        const msg = notif.message.replace(/<[^>]*>/g, '').trim();
        setFlashMessage(msg);
        setShowFlash(true);
        playSound();

        setTimeout(() => {
          setShowFlash(false);
          setTimeout(() => setFlashMessage(''), 300);
        }, 4000);
      }
    });

    socketRef.current = socket;
    fetchUnreadCount();

    return () => {
      if (!audioUnlocked.current) {
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('touchstart', unlockAudio);
      }
      socket.close();
    };
  }, [location.pathname]);

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch('https://instant-transfer-back-production.up.railway.app/api/admin/notifications/unread-count');
      const data = await res.json();
      setUnreadCount(data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to fetch count:', err);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && showConfirmModal) {
        setShowConfirmModal(false);
      }
    };

    if (showConfirmModal) {
      document.addEventListener('keydown', onKey);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = prevOverflow || '';
      };
    }

    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [showConfirmModal]);

  return (
    <>
      <audio ref={audioRef} src="/assets/notification.mp3" preload="auto" />

      {/* Flash notification toast */}
      <div
        className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 pointer-events-none ${
          showFlash ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'
        }`}
      >
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-pulse pointer-events-auto">
          <span className="material-icons text-xl">notifications_active</span>
          <span className="font-medium text-sm max-w-xs truncate">{flashMessage}</span>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-48 bg-farmer-dark text-white p-3 transform transition-transform duration-300 ease-in-out z-[60] md:relative md:transform-none ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        style={{ touchAction: 'manipulation' }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold tracking-tight font-sftsMedium">Instant Transfer</h2>
          <button className="text-white md:hidden" onClick={toggleSidebar}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav>
          <ul>
            <li className="mb-2">
              <NavLink
                to="/overview"
                exact
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 transition-colors font-sftsMedium"
                activeClassName="bg-gray-700 text-lightgreen"
                onClick={toggleSidebar}
              >
                <span className="material-icons text-blue-400 mr-2">dashboard</span>
                Overview
              </NavLink>
            </li>

            <li className="mb-2">
              <NavLink
                to="/users"
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 transition-colors font-sftsMedium"
                activeClassName="bg-gray-700 text-lightgreen"
                onClick={toggleSidebar}
              >
                <span className="material-icons text-green-400 mr-2">people</span>
                Users
              </NavLink>
            </li>

            {/* Regular Transactions */}
            <li className="mb-2">
              <NavLink
                to="/transactions"
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 transition-colors font-sftsMedium"
                activeClassName="bg-gray-700 text-lightgreen"
                onClick={toggleSidebar}
              >
                <span className="material-icons text-yellow-400 mr-2">attach_money</span>
                Transactions
              </NavLink>
            </li>

            {/* NEW: MT5 Transactions */}
            <li className="mb-2">
              <NavLink
                to="/mt5-transactions"
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 transition-colors font-sftsMedium"
                activeClassName="bg-gray-700 text-lightgreen"
                onClick={toggleSidebar}
              >
                <span className="material-icons text-cyan-400 mr-2">trending_up</span>
                MT5 
              </NavLink>
            </li>

            {/* NEW: Bank Transactions */}
            <li className="mb-2">
              <NavLink
                to="/bank-transactions"
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 transition-colors font-sftsMedium"
                activeClassName="bg-gray-700 text-lightgreen"
                onClick={toggleSidebar}
              >
                <span className="material-icons text-indigo-400 mr-2">account_balance</span>
                Bank 
              </NavLink>
            </li>

            <li className="mb-2">
              <NavLink
                to="/notifications"
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 transition-colors font-sftsMedium relative"
                activeClassName="bg-gray-700 text-lightgreen"
                onClick={() => {
                  toggleSidebar();
                  setUnreadCount(0);
                }}
              >
                <span className="material-icons text-purple-400 mr-2">notifications</span>
                <span className="flex-1">Notifications</span>
                {unreadCount > 0 && (
                  <span className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-red-600 text-white text-xs font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center shadow-md animate-pulse">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </NavLink>
            </li>

            <li className="mb-2">
              <NavLink
                to="/settings"
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 transition-colors font-sftsMedium"
                activeClassName="bg-gray-700 text-lightgreen"
                onClick={toggleSidebar}
              >
                <span className="material-icons text-gray-300 mr-2">settings</span>
                Settings
              </NavLink>
            </li>

            <li className="mb-2">
              <button
                onClick={() => setShowConfirmModal(true)}
                className="flex items-center py-1 px-2 text-sm rounded hover:bg-gray-700 hover:text-lightgreen transition-colors text-left w-full font-sftsMedium"
              >
                <span className="material-icons text-red-400 mr-2">logout</span>
                Logout
              </button>
            </li>
          </ul>
        </nav>

        {/* Fonts & icons styles */}
        <style jsx>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600&family=Open+Sans:wght@400&family=Material+Icons&display=swap');
          .material-icons {
            font-size: 20px;
            display: inline-flex;
            align-items: center;
          }
        `}</style>
      </div>

      {/* Logout confirmation modal */}
      {showConfirmModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] transition-opacity duration-200"
            onClick={() => setShowConfirmModal(false)}
            aria-hidden="true"
          />

          <div className="fixed inset-0 flex items-center justify-center z-[110] px-4">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="logout-modal-title"
              className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl w-full max-w-sm transform transition-all duration-200 ease-out scale-100"
            >
              <h3 id="logout-modal-title" className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
                Confirm Logout
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to log out?
              </p>

              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    handleLogout();
                  }}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Navbar;